import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { Plus, Search, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const TeamManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const queryClient = useQueryClient();

  const limit = 20;

  // Query para obtener equipos
  const { data: teamsData, isLoading, error } = useQuery({
    queryKey: ['teams-management', searchTerm, currentPage],
    queryFn: () => api.get(`/teams?search=${searchTerm}&limit=${limit}&offset=${currentPage * limit}`).then(res => res.data),
    keepPreviousData: true,
    staleTime: 30000
  });

  const teams = teamsData?.teams || [];
  const pagination = teamsData?.pagination || {};

  // Mutation para crear equipo
  const createTeamMutation = useMutation({
    mutationFn: (teamData) => api.post('/teams', teamData),
    onSuccess: (response) => {
      toast.success('Equipo creado exitosamente');
      queryClient.invalidateQueries(['teams-management']);
      queryClient.invalidateQueries(['teams']); // Invalidar cache del selector también
      setShowCreateForm(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error creando el equipo');
    }
  });

  // Mutation para actualizar equipo
  const updateTeamMutation = useMutation({
    mutationFn: ({ id, ...teamData }) => api.put(`/teams/${id}`, teamData),
    onSuccess: () => {
      toast.success('Equipo actualizado exitosamente');
      queryClient.invalidateQueries(['teams-management']);
      queryClient.invalidateQueries(['teams']);
      setEditingTeam(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error actualizando el equipo');
    }
  });

  // Mutation para desactivar/activar equipo
  const toggleTeamMutation = useMutation({
    mutationFn: (team) => api.put(`/teams/${team.id}`, { ...team, isActive: !team.is_active }),
    onSuccess: (response, variables) => {
      const action = variables.is_active ? 'desactivado' : 'activado';
      toast.success(`Equipo ${action} exitosamente`);
      queryClient.invalidateQueries(['teams-management']);
      queryClient.invalidateQueries(['teams']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error actualizando el equipo');
    }
  });

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
  };

  const handleToggleActive = (team) => {
    if (window.confirm(`¿Estás seguro de ${team.is_active ? 'desactivar' : 'activar'} el equipo "${team.name}"?`)) {
      toggleTeamMutation.mutate(team);
    }
  };

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Error cargando equipos</h3>
        <p className="text-red-600">{error.response?.data?.message || 'No se pudieron cargar los equipos'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestión de Equipos
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Administra la base de datos de equipos del sistema
          </p>
        </div>

        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuevo Equipo
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Buscar equipos por nombre o nombre corto..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Lista de equipos */}
      <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3"></div>
              <span className="text-gray-600 dark:text-gray-400">Cargando equipos...</span>
            </div>
          </div>
        ) : teams.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-500 dark:text-gray-400">
              {searchTerm ? `No se encontraron equipos para "${searchTerm}"` : 'No hay equipos registrados'}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Equipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      País/Liga
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                  {teams.map(team => (
                    <tr key={team.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {team.logo_url && (
                            <img
                              src={team.logo_url}
                              className="w-8 h-8 object-contain mr-3"
                              alt=""
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {team.name}
                            </div>
                            {team.short_name && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {team.short_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {team.country || '-'}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {team.league || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          team.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {team.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => setEditingTeam(team)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(team)}
                            className={`${
                              team.is_active
                                ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                                : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                            title={team.is_active ? 'Desactivar' : 'Activar'}
                          >
                            {team.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-dark-600">
              {teams.map(team => (
                <div key={team.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      {team.logo_url && (
                        <img
                          src={team.logo_url}
                          className="w-6 h-6 object-contain mr-2"
                          alt=""
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {team.name}
                        </div>
                        {team.short_name && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {team.short_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      team.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {team.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {team.country && team.league ? `${team.country} • ${team.league}` : team.country || team.league || 'Sin ubicación'}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingTeam(team)}
                      className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-900 dark:text-blue-400"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </button>
                    <button
                      onClick={() => handleToggleActive(team)}
                      className={`flex items-center px-3 py-1 text-sm ${
                        team.is_active
                          ? 'text-red-600 hover:text-red-900 dark:text-red-400'
                          : 'text-green-600 hover:text-green-900 dark:text-green-400'
                      }`}
                    >
                      {team.is_active ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1" />
                          Desactivar
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" />
                          Activar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Paginación */}
        {pagination.total > limit && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Mostrando {currentPage * limit + 1} a {Math.min((currentPage + 1) * limit, pagination.total)} de {pagination.total} equipos
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-dark-500 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-dark-600"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.hasMore}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-dark-500 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-dark-600"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modales */}
      {showCreateForm && (
        <TeamFormModal
          title="Crear Nuevo Equipo"
          onSubmit={(data) => createTeamMutation.mutate(data)}
          onClose={() => setShowCreateForm(false)}
          isLoading={createTeamMutation.isLoading}
        />
      )}

      {editingTeam && (
        <TeamFormModal
          title="Editar Equipo"
          team={editingTeam}
          onSubmit={(data) => updateTeamMutation.mutate({ id: editingTeam.id, ...data })}
          onClose={() => setEditingTeam(null)}
          isLoading={updateTeamMutation.isLoading}
        />
      )}
    </div>
  );
};

// Componente del modal de formulario
const TeamFormModal = ({ title, team, onSubmit, onClose, isLoading }) => {
  const [formData, setFormData] = useState({
    name: team?.name || '',
    shortName: team?.short_name || '',
    country: team?.country || 'Colombia',
    league: team?.league || 'Liga BetPlay',
    logoUrl: team?.logo_url || '',
    isActive: team?.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      shortName: formData.shortName || null,
      country: formData.country || null,
      league: formData.league || null,
      logoUrl: formData.logoUrl || null,
      isActive: formData.isActive
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            {title}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre completo *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre corto
              </label>
              <input
                type="text"
                value={formData.shortName}
                onChange={(e) => setFormData(prev => ({ ...prev, shortName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  País
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Liga
                </label>
                <input
                  type="text"
                  value={formData.league}
                  onChange={(e) => setFormData(prev => ({ ...prev, league: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL del logo
              </label>
              <input
                type="url"
                value={formData.logoUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, logoUrl: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-500 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100"
                placeholder="https://ejemplo.com/logo.png"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                Equipo activo
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading || !formData.name.trim()}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Guardando...' : team ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;